import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { axiosInstance, BASE_API_CONFIG } from "../util/Api";
import { DEFAULT_ENQUIRY_CREDIT_CONFIG } from "../util/Constant";
import { isFreightCreditUser } from "../util/helper";

export const WALLET_SUMMARY_UPDATED_EVENT = "wallet-summary-updated";

export const notifyWalletSummaryUpdated = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(WALLET_SUMMARY_UPDATED_EVENT));
  }
};

const defaultSummary = {
  availableBalance: 0,
  freeFunds: DEFAULT_ENQUIRY_CREDIT_CONFIG.freeFunds,
  freeFundsUsed: 0,
  freeFundsRemaining: DEFAULT_ENQUIRY_CREDIT_CONFIG.freeFunds,
  freeEnquiries: DEFAULT_ENQUIRY_CREDIT_CONFIG.freeEnquiries,
  freeEnquiriesUsed: 0,
  freeEnquiriesRemaining: DEFAULT_ENQUIRY_CREDIT_CONFIG.freeEnquiries,
  freePorts: DEFAULT_ENQUIRY_CREDIT_CONFIG.freePorts,
  freePortsUsed: 0,
  freePortsRemaining: DEFAULT_ENQUIRY_CREDIT_CONFIG.freePorts,
  perEnquiryCharge: DEFAULT_ENQUIRY_CREDIT_CONFIG.perEnquiryCharge,
  minAddFundsAmount: DEFAULT_ENQUIRY_CREDIT_CONFIG.minAddFundsAmount,
  maxAddFundsAmount: DEFAULT_ENQUIRY_CREDIT_CONFIG.maxAddFundsAmount,
  currency: DEFAULT_ENQUIRY_CREDIT_CONFIG.currency,
  lifetimeUnlockedEnquiryIds: [],
  history: [],
};

const normalizeWalletSummary = (payload = {}, fallbackBalance = 0) => ({
  ...defaultSummary,
  ...payload,
  availableBalance:
    payload.availableBalance ??
    payload.walletBalance ??
    payload.points ??
    fallbackBalance,
  freeFunds:
    payload.freeFunds ?? payload.freeCredits ?? defaultSummary.freeFunds,
  freeFundsUsed: payload.freeFundsUsed ?? payload.freeCreditsUsed ?? 0,
  freeFundsRemaining:
    payload.freeFundsRemaining ??
    payload.freeCreditsRemaining ??
    Math.max(
      (payload.freeFunds ?? payload.freeCredits ?? defaultSummary.freeFunds) -
        (payload.freeFundsUsed ?? payload.freeCreditsUsed ?? 0),
      0
    ),
  freeEnquiries:
    payload.freeEnquiries ?? defaultSummary.freeEnquiries,
  freeEnquiriesUsed: payload.freeEnquiriesUsed ?? 0,
  freeEnquiriesRemaining:
    payload.freeEnquiriesRemaining ??
    Math.max(
      (payload.freeEnquiries ?? defaultSummary.freeEnquiries) -
        (payload.freeEnquiriesUsed ?? 0),
      0
    ),
  freePorts: payload.freePorts ?? defaultSummary.freePorts,
  freePortsUsed: payload.freePortsUsed ?? 0,
  freePortsRemaining:
    payload.freePortsRemaining ??
    Math.max(
      (payload.freePorts ?? defaultSummary.freePorts) -
        (payload.freePortsUsed ?? 0),
      0
    ),
  perEnquiryCharge:
    payload.perEnquiryCharge ?? defaultSummary.perEnquiryCharge,
  minAddFundsAmount:
    payload.minAddFundsAmount ?? defaultSummary.minAddFundsAmount,
  maxAddFundsAmount:
    payload.maxAddFundsAmount ?? defaultSummary.maxAddFundsAmount,
  currency: payload.currency ?? defaultSummary.currency,
  lifetimeUnlockedEnquiryIds:
    payload.lifetimeUnlockedEnquiryIds ?? payload.accessedEnquiryIds ?? [],
  history: payload.history ?? [],
});

export const useWalletSummary = () => {
  const authUser = useSelector(({ auth }) => auth?.userData);
  const uuid = authUser?.uuid;
  const isFreightUser = isFreightCreditUser(authUser);
  const [summary, setSummary] = useState(defaultSummary);
  const [loading, setLoading] = useState(false);

  const fetchWalletSummary = useCallback(async () => {
    if (!uuid || !isFreightUser) {
      setSummary(defaultSummary);
      return defaultSummary;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.get(
        `${BASE_API_CONFIG.baseURL}/wallet/summary/${uuid}`
      );
      const payload = response?.data?.data ?? response?.data ?? {};
      const normalized = normalizeWalletSummary(payload);
      setSummary(normalized);
      return normalized;
    } catch (walletError) {
      try {
        const response = await axiosInstance.get(
          `${BASE_API_CONFIG.baseURL}/points/${uuid}`
        );
        const fallbackBalance = response?.data?.data?.points ?? 0;
        const normalized = normalizeWalletSummary({}, fallbackBalance);
        setSummary(normalized);
        return normalized;
      } catch (pointsError) {
        setSummary(defaultSummary);
        return defaultSummary;
      }
    } finally {
      setLoading(false);
    }
  }, [isFreightUser, uuid]);

  useEffect(() => {
    fetchWalletSummary();
  }, [fetchWalletSummary]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleWalletSummaryUpdated = () => {
      fetchWalletSummary();
    };

    window.addEventListener(
      WALLET_SUMMARY_UPDATED_EVENT,
      handleWalletSummaryUpdated
    );

    return () => {
      window.removeEventListener(
        WALLET_SUMMARY_UPDATED_EVENT,
        handleWalletSummaryUpdated
      );
    };
  }, [fetchWalletSummary]);

  return {
    ...summary,
    loading,
    refresh: fetchWalletSummary,
  };
};
